import React, { useEffect, useState, useRef } from "react";
import "../styles/Drones.css";
import { useQuery, useMutation, gql } from "@apollo/client";

const ITEMS_PER_PAGE = 10;
const PRODUCTS_PER_PAGE = 10;

// GraphQL Definitions
const GET_DRONES = gql`
  query {
    drones {
      droneId
      name
      brand
      uin
      price
      description
      image
      quantity
      status
      sellerId
      sellerInfo {
        email
        phoneNumber
      }
      weight
      length
      breadth
      height
    }
  }
`;

const UPDATE_DRONE_STATUS = gql`
  mutation UpdateDroneStatus($droneId: String!, $status: String!) {
    updateDroneStatus(droneId: $droneId, status: $status) {
      droneId
      status
    }
  }
`;

const UPDATE_DRONE = gql`
  mutation UpdateDrone($droneId: String!, $name: String!, $brand: String!, $price: Float!, $quantity: Int!, $description: String!, $weight: Float, $length: Float, $breadth: Float, $height: Float) {
    updateDrone(droneId: $droneId, name: $name, brand: $brand, price: $price, quantity: $quantity, description: $description, weight: $weight, length: $length, breadth: $breadth, height: $height) {
      droneId
      name
      brand
      price
      quantity
      description
      weight
      length
      breadth
      height
    }
  }
`;

const DELETE_DRONE = gql`
  mutation DeleteDrone($droneId: String!) {
    deleteDrone(droneId: $droneId) {
      droneId
    }
  }
`;

function Drones() {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [expandedSeller, setExpandedSeller] = useState(null);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "sellerId", direction: "asc" });
  const [viewingDrone, setViewingDrone] = useState(null);
  const [viewingDescription, setViewingDescription] = useState(null);
  const [editingDrone, setEditingDrone] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPage, setProductsPage] = useState({});
  const tableRef = useRef(null);

  // Apollo Hooks
  const { loading, error: queryError, data, refetch } = useQuery(GET_DRONES, {
    fetchPolicy: "network-only",
  });

  const [updateStatusMutation] = useMutation(UPDATE_DRONE_STATUS);
  const [updateDroneMutation] = useMutation(UPDATE_DRONE);
  const [deleteDroneMutation] = useMutation(DELETE_DRONE);

  const drones = data?.drones || [];
  const error = queryError ? queryError.message : null;

  // Group drones by seller ID
  const groupedBySeller = drones.reduce((acc, drone) => {
    const sellerId = drone.sellerId;
    if (!acc[sellerId]) {
      acc[sellerId] = {
        sellerId: sellerId,
        sellerInfo: drone.sellerInfo,
        drones: []
      };
    }
    acc[sellerId].drones.push(drone);
    return acc;
  }, {});

  // Convert to array and sort
  const sellerGroups = Object.values(groupedBySeller).sort((a, b) =>
    a.sellerId.localeCompare(b.sellerId)
  );

  // Calculate seller stats
  const sellerGroupsWithStats = sellerGroups.map(seller => {
    const totalValue = seller.drones.reduce((sum, d) => sum + (d.price * d.quantity), 0);
    const totalStock = seller.drones.reduce((sum, d) => sum + d.quantity, 0);
    const approvedDrones = seller.drones.filter(d => d.status === 'approved').length;
    const pendingDrones = seller.drones.filter(d => d.status === 'pending').length;
    const rejectedDrones = seller.drones.filter(d => d.status === 'rejected').length;

    return {
      ...seller,
      totalValue,
      totalStock,
      approvedDrones,
      pendingDrones,
      rejectedDrones,
      totalDrones: seller.drones.length
    };
  });

  // Filter sellers based on search
  const filteredSellers = sellerGroupsWithStats.filter(seller => {
    const matchesSearch =
      searchTerm === "" ||
      seller.sellerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      seller.sellerInfo?.phoneNumber?.includes(searchTerm);

    if (selectedStatus === "all") return matchesSearch;

    // Filter by status
    const filteredDrones = seller.drones.filter(drone =>
      drone.status.toLowerCase() === selectedStatus
    );

    return matchesSearch && filteredDrones.length > 0;
  });

  // Sort sellers
  const sortedSellers = [...filteredSellers].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key];
    let bVal = b[key];

    if (key === 'sellerId' || key === 'email') {
      aVal = aVal?.toLowerCase?.() || '';
      bVal = bVal?.toLowerCase?.() || '';
    }

    if (sortConfig.direction === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  // Pagination for sellers
  const totalFilteredPages = Math.ceil(sortedSellers.length / ITEMS_PER_PAGE);
  const paginatedSellers = sortedSellers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get filtered drones for expanded seller with pagination
  const getFilteredDronesForSeller = (sellerId) => {
    const seller = groupedBySeller[sellerId];
    if (!seller) return [];

    return seller.drones.filter(drone => {
      return selectedStatus === "all" || drone.status.toLowerCase() === selectedStatus;
    });
  };

  // Get paginated products for a seller
  const getPaginatedProductsForSeller = (sellerId) => {
    const filteredDrones = getFilteredDronesForSeller(sellerId);
    const currentProductsPage = productsPage[sellerId] || 1;
    const startIndex = (currentProductsPage - 1) * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;

    return {
      products: filteredDrones.slice(startIndex, endIndex),
      currentPage: currentProductsPage,
      totalPages: Math.ceil(filteredDrones.length / PRODUCTS_PER_PAGE),
      totalProducts: filteredDrones.length
    };
  };

  // Handle products page change
  const handleProductsPageChange = (sellerId, page) => {
    setProductsPage(prev => ({
      ...prev,
      [sellerId]: page
    }));
  };

  // Update drone status
  const updateDroneStatus = async (droneId, newStatus) => {
    const updatingIdsCopy = new Set(updatingIds);
    updatingIdsCopy.add(`${droneId}-${newStatus}`);
    setUpdatingIds(updatingIdsCopy);

    try {
      await updateStatusMutation({
        variables: { droneId, status: newStatus.toLowerCase() },
      });
      refetch();
    } catch (err) {
      alert(`Error updating status: ${err.message}`);
    } finally {
      const updatingIdsCopy = new Set(updatingIds);
      updatingIdsCopy.delete(`${droneId}-${newStatus}`);
      setUpdatingIds(updatingIdsCopy);
    }
  };

  // Delete drone
  const deleteDrone = async (droneId) => {
    if (!window.confirm("Are you sure you want to delete this drone?")) return;

    try {
      await deleteDroneMutation({
        variables: { droneId },
      });
      refetch();
    } catch (err) {
      alert(`Error deleting drone: ${err.message}`);
    }
  };

  // Save edited drone
  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingDrone) return;

    try {
      await updateDroneMutation({
        variables: {
          droneId: editingDrone.droneId,
          name: editingDrone.name,
          brand: editingDrone.brand,
          price: parseFloat(editingDrone.price),
          quantity: parseInt(editingDrone.quantity),
          description: editingDrone.description,
          weight: parseFloat(editingDrone.weight) || 0,
          length: parseFloat(editingDrone.length) || 0,
          breadth: parseFloat(editingDrone.breadth) || 0,
          height: parseFloat(editingDrone.height) || 0
        },
      });
      setEditingDrone(null);
      refetch();
    } catch (err) {
      alert(`Error updating drone: ${err.message}`);
    }
  };

  // View drone details with actions
  const viewDroneDetails = (drone) => {
    setViewingDrone(drone);
  };

  // Close drone details modal
  const closeDroneDetails = () => {
    setViewingDrone(null);
  };

  // View description only
  const viewDescription = (drone) => {
    setViewingDescription(drone);
  };

  // Close description modal
  const closeDescription = () => {
    setViewingDescription(null);
  };

  // Handle edit drone
  const handleEditDrone = (drone) => {
    setEditingDrone({ ...drone });
  };

  // Handle seller page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (tableRef.current) {
      tableRef.current.scrollLeft = 0;
    }
  };

  const statusStats = {
    all: drones.length,
    approved: drones.filter(d => d.status === "approved").length,
    pending: drones.filter(d => d.status === "pending").length,
    rejected: drones.filter(d => d.status === "rejected").length,
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return <span style={{ marginLeft: '4px', fontSize: '12px' }}>{sortConfig.direction === "asc" ? "↑" : "↓"}</span>;
  };

  const truncateDescription = (description, length = 30) => {
    if (!description) return "No description";
    if (description.length <= length) return description;
    return description.substring(0, length) + "...";
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Calculate overall stats
  const totalValue = drones.reduce((sum, d) => sum + (d.price * d.quantity), 0);
  const totalStock = drones.reduce((sum, d) => sum + d.quantity, 0);
  const approvedDrones = drones.filter(d => d.status === 'approved').length;
  const pendingDrones = drones.filter(d => d.status === 'pending').length;

  return (
    <div className="drones-container">
      <div className="main-wrapper">
        {/* Top Navigation */}
        <div className="top-navigation">
          <div className="nav-left">
            <h1>Drone Management System</h1>
            <p>Admin Dashboard • Manage Seller Products • Real-time Updates</p>
          </div>
          <div className="nav-right">
            <button
              className="btn btn-secondary"
              onClick={() => refetch()}
              disabled={loading}
            >
              {loading ? "🔄 Refreshing..." : "🔄 Refresh"}
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const headers = ["Drone ID", "Seller ID", "Name", "Brand", "UIN", "Price", "Quantity", "Status", "Description"];
                const rows = drones.map(drone => [
                  drone.droneId,
                  drone.sellerId,
                  drone.name,
                  drone.brand,
                  drone.uin,
                  drone.price,
                  drone.quantity,
                  drone.status,
                  drone.description
                ]);

                let csvContent = headers.join(',') + '\n';
                rows.forEach(row => {
                  csvContent += row.map(cell => `"${cell}"`).join(',') + '\n';
                });

                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `drones_export_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              📊 Export Report
            </button>
          </div>
        </div>

        {/* Plain Stats Dashboard (like Parts page) */}
        <div className="compact-stats-container">
          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Inventory Value</div>
              <div className="compact-stat-value">₹{totalValue.toLocaleString('en-IN')}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Stock Units</div>
              <div className="compact-stat-value">{totalStock}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Total Sellers</div>
              <div className="compact-stat-value">{sellerGroups.length}</div>
            </div>
          </div>

          <div className="compact-stat-card">
            <div className="compact-stat-content">
              <div className="compact-stat-label">Pending Approval</div>
              <div className="compact-stat-value">{pendingDrones}</div>
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="control-panel">
          <div className="panel-header">
            <h2 className="panel-title">Sellers Management</h2>
          </div>

          <div className="seller-selector">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search sellers by ID, email, or phone..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
              />

            </div>

            <div className="status-tabs-container">
              {["all", "approved", "pending", "rejected"].map((status) => (
                <button
                  key={status}
                  className={`status-tab ${selectedStatus === status ? "active" : ""}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setCurrentPage(1);
                    // Reset product pages when status changes
                    setProductsPage({});
                  }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  <span className="tab-count">{statusStats[status]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <span>{error}</span>
            <button onClick={() => refetch()}>✕</button>
          </div>
        )}

        {/* Main Content Area */}
        <div className="content-area">
          {/* Sellers Table Container */}
          <div className="table-container">
            <div className="table-header">
              <h3 className="table-title">
                All Sellers ({sellerGroups.length})
              </h3>
              <div className="table-controls">
                <div className="table-info">
                  Page {currentPage} of {totalFilteredPages || 1} • Showing {paginatedSellers.length} sellers
                </div>
              </div>
            </div>

            <div className="table-content" ref={tableRef}>
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading seller data...</p>
                </div>
              ) : sellerGroups.length === 0 ? (
                <div className="empty-state">
                  <p>No sellers found</p>
                </div>
              ) : (
                <table className="sellers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort("sellerId")}>
                        Seller ID <SortIcon column="sellerId" />
                      </th>
                      <th onClick={() => handleSort("email")}>
                        Contact Info <SortIcon column="email" />
                      </th>
                      <th onClick={() => handleSort("totalDrones")}>
                        Products <SortIcon column="totalDrones" />
                      </th>
                      <th onClick={() => handleSort("totalValue")}>
                        Inventory Value <SortIcon column="totalValue" />
                      </th>
                      <th onClick={() => handleSort("totalStock")}>
                        Total Stock <SortIcon column="totalStock" />
                      </th>
                      <th>Status Breakdown</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedSellers.map((seller) => {
                      const { products, currentPage: productsCurrentPage, totalPages: productsTotalPages, totalProducts } = getPaginatedProductsForSeller(seller.sellerId);

                      return (
                        <React.Fragment key={seller.sellerId}>
                          <tr
                            className={`seller-row ${expandedSeller === seller.sellerId ? 'expanded' : ''}`}
                            onClick={() => setExpandedSeller(
                              expandedSeller === seller.sellerId ? null : seller.sellerId
                            )}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <div className="seller-id-cell">
                                <span className="expand-icon">
                                  {expandedSeller === seller.sellerId ? '▼' : '▶'}
                                </span>
                                <strong>Seller #{seller.sellerId}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-contact-cell">
                                <div className="seller-email">
                                  📧 {seller.sellerInfo?.email || "No email"}
                                </div>
                                <div className="seller-phone">
                                  📱 {seller.sellerInfo?.phoneNumber || "No phone"}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="seller-products-count">
                                <span className="count-badge">{seller.totalDrones}</span>
                                <span className="count-label">products</span>
                              </div>
                            </td>
                            <td>
                              <div className="seller-value">
                                <strong className="value-amount">
                                  ₹{seller.totalValue.toLocaleString('en-IN')}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <div className="seller-stock">
                                <span className="stock-count">{seller.totalStock}</span>
                                <span className="stock-label">units</span>
                              </div>
                            </td>
                            <td>
                              <div className="status-breakdown">
                                <span className="status-dot approved" title={`${seller.approvedDrones} approved`}>
                                  {seller.approvedDrones}
                                </span>
                                <span className="status-dot pending" title={`${seller.pendingDrones} pending`}>
                                  {seller.pendingDrones}
                                </span>
                                <span className="status-dot rejected" title={`${seller.rejectedDrones} rejected`}>
                                  {seller.rejectedDrones}
                                </span>
                              </div>
                            </td>
                            <td>
                              <button
                                className="action-btn action-view"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setExpandedSeller(
                                    expandedSeller === seller.sellerId ? null : seller.sellerId
                                  );
                                }}
                                title="View Products"
                              >
                                {expandedSeller === seller.sellerId ? '👁️‍🗨️' : '👁️'}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Products Row */}
                          {expandedSeller === seller.sellerId && (
                            <tr className="products-row">
                              <td colSpan="7">
                                <div className="products-container">
                                  <div className="products-header">
                                    <div>
                                      <h4>Products from Seller #{seller.sellerId}</h4>
                                      <span className="products-count">
                                        {totalProducts} products • Showing {products.length} on page {productsCurrentPage} of {productsTotalPages}
                                      </span>
                                    </div>
                                  </div>

                                  {totalProducts === 0 ? (
                                    <div className="no-products">
                                      <p>No products found for the selected status filter.</p>
                                    </div>
                                  ) : (
                                    <>
                                      <div className="products-table-container">
                                        <table className="products-table">
                                          <thead>
                                            <tr>
                                              <th>Product ID</th>
                                              <th>Product Details</th>
                                              <th>Brand</th>
                                              <th>UIN</th>
                                              <th>Price</th>
                                              <th>Stock</th>
                                              <th>Status</th>
                                              <th>Description</th>
                                              <th>Actions</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {products.map((drone) => (
                                              <tr key={drone.droneId}>
                                                <td>
                                                  <div className="id-cell">
                                                    {drone.droneId}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-details-cell">
                                                    {drone.image && (
                                                      <img src={drone.image} alt={drone.name} className="drone-image" />
                                                    )}
                                                    <div>
                                                      <div className="drone-name">{drone.name}</div>
                                                      <div className="drone-specs">
                                                        <span className="drone-brand">{drone.brand}</span>
                                                      </div>
                                                    </div>
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="drone-brand">{drone.brand}</div>
                                                </td>
                                                <td>
                                                  <div className="drone-uin">{drone.uin}</div>
                                                </td>
                                                <td>
                                                  <div className="price-cell">
                                                    {formatPrice(drone.price)}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className={`quantity-cell ${drone.quantity === 0 ? 'out-of-stock' : ''}`}>
                                                    {drone.quantity}
                                                    {drone.quantity < 10 && drone.quantity > 0 && (
                                                      <span className="low-stock-indicator">!</span>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <span className={`status-badge status-${drone.status}`}>
                                                    {drone.status.toUpperCase()}
                                                  </span>
                                                </td>
                                                <td>
                                                  <div>
                                                    {truncateDescription(drone.description, 30)}
                                                    {drone.description && drone.description.length > 30 && (
                                                      <button
                                                        onClick={() => viewDescription(drone)}
                                                        className="read-more-btn"
                                                      >
                                                        Read more
                                                      </button>
                                                    )}
                                                  </div>
                                                </td>
                                                <td>
                                                  <div className="action-buttons">
                                                    {drone.status === "pending" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateDroneStatus(drone.droneId, "approved")}
                                                          disabled={updatingIds.has(`${drone.droneId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${drone.droneId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateDroneStatus(drone.droneId, "rejected")}
                                                          disabled={updatingIds.has(`${drone.droneId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${drone.droneId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {drone.status === "approved" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateDroneStatus(drone.droneId, "pending")}
                                                          disabled={updatingIds.has(`${drone.droneId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${drone.droneId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-reject"
                                                          onClick={() => updateDroneStatus(drone.droneId, "rejected")}
                                                          disabled={updatingIds.has(`${drone.droneId}-rejected`)}
                                                          title="Reject"
                                                        >
                                                          {updatingIds.has(`${drone.droneId}-rejected`) ? "..." : "✗"}
                                                        </button>
                                                      </>
                                                    )}

                                                    {drone.status === "rejected" && (
                                                      <>
                                                        <button
                                                          className="action-btn action-pending"
                                                          onClick={() => updateDroneStatus(drone.droneId, "pending")}
                                                          disabled={updatingIds.has(`${drone.droneId}-pending`)}
                                                          title="Move to Pending"
                                                        >
                                                          {updatingIds.has(`${drone.droneId}-pending`) ? "..." : "⏳"}
                                                        </button>
                                                        <button
                                                          className="action-btn action-approve"
                                                          onClick={() => updateDroneStatus(drone.droneId, "approved")}
                                                          disabled={updatingIds.has(`${drone.droneId}-approved`)}
                                                          title="Approve"
                                                        >
                                                          {updatingIds.has(`${drone.droneId}-approved`) ? "..." : "✓"}
                                                        </button>
                                                      </>
                                                    )}

                                                    <button
                                                      className="action-btn action-view"
                                                      onClick={() => viewDroneDetails(drone)}
                                                      title="View Details"
                                                    >
                                                      👁
                                                    </button>



                                                    <button
                                                      className="action-btn action-delete"
                                                      onClick={() => deleteDrone(drone.droneId)}
                                                      title="Delete"
                                                    >
                                                      🗑
                                                    </button>
                                                  </div>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>

                                      {/* Products Pagination */}
                                      {productsTotalPages > 1 && (
                                        <div className="products-pagination">
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage - 1)}
                                            disabled={productsCurrentPage === 1}
                                            className="pagination-btn"
                                          >
                                            &lt; Previous
                                          </button>
                                          <div className="pagination-info">
                                            Page {productsCurrentPage} of {productsTotalPages}
                                          </div>
                                          <button
                                            onClick={() => handleProductsPageChange(seller.sellerId, productsCurrentPage + 1)}
                                            disabled={productsCurrentPage === productsTotalPages}
                                            className="pagination-btn"
                                          >
                                            Next &gt;
                                          </button>
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls for Sellers */}
            {totalFilteredPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalFilteredPages }, (_, i) => i + 1).map((num) => (
                  <button
                    key={num}
                    className={currentPage === num ? "active" : ""}
                    onClick={() => handlePageChange(num)}
                  >
                    {num}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalFilteredPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drone Details Modal with Actions */}
      {viewingDrone && (
        <div className="modal-overlay" onClick={closeDroneDetails}>
          <div className="modal-content drone-details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Drone Details</h2>
              <button className="close-btn" onClick={closeDroneDetails}>×</button>
            </div>
            <div className="modal-body">
              <div className="drone-details-grid">
                <div className="drone-image-section">
                  {viewingDrone.image ? (
                    <img
                      src={viewingDrone.image}
                      alt={viewingDrone.name}
                      className="drone-detail-image"
                    />
                  ) : (
                    <div className="no-image-placeholder">No Image</div>
                  )}
                </div>

                <div className="drone-info-section">
                  <div className="info-row">
                    <div className="info-label">Drone ID</div>
                    <div className="info-value">{viewingDrone.droneId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Name</div>
                    <div className="info-value">{viewingDrone.name}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Brand</div>
                    <div className="info-value">{viewingDrone.brand}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">UIN</div>
                    <div className="info-value">{viewingDrone.uin}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Price</div>
                    <div className="info-value price-value">{formatPrice(viewingDrone.price)}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Dimensions</div>
                    <div className="info-value">
                      {viewingDrone.length || 0} x {viewingDrone.breadth || 0} x {viewingDrone.height || 0} cm
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Weight</div>
                    <div className="info-value">{viewingDrone.weight || 0} kg</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Quantity</div>
                    <div className="info-value">
                      <span className={`quantity-display ${viewingDrone.quantity === 0 ? 'out-of-stock' : ''}`}>
                        {viewingDrone.quantity}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Status</div>
                    <div className="info-value">
                      <span className={`status-badge status-${viewingDrone.status}`}>
                        {viewingDrone.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller ID</div>
                    <div className="info-value">{viewingDrone.sellerId}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Email</div>
                    <div className="info-value">{viewingDrone.sellerInfo?.email || "N/A"}</div>
                  </div>
                  <div className="info-row">
                    <div className="info-label">Seller Phone</div>
                    <div className="info-value">{viewingDrone.sellerInfo?.phoneNumber || "N/A"}</div>
                  </div>
                </div>

                <div className="drone-description-section">
                  <div className="info-label">Description</div>
                  <div className="description-content">
                    {viewingDrone.description || 'No description available'}
                  </div>
                </div>

                {/* Actions in Details Modal */}
                <div className="drone-actions-section">
                  <div className="info-label">Quick Actions</div>
                  <div className="action-buttons-grid">
                    {viewingDrone.status === "pending" && (
                      <>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateDroneStatus(viewingDrone.droneId, "approved");
                            closeDroneDetails();
                          }}
                          disabled={updatingIds.has(`${viewingDrone.droneId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingDrone.droneId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateDroneStatus(viewingDrone.droneId, "rejected");
                            closeDroneDetails();
                          }}
                          disabled={updatingIds.has(`${viewingDrone.droneId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingDrone.droneId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    {viewingDrone.status === "approved" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateDroneStatus(viewingDrone.droneId, "pending");
                            closeDroneDetails();
                          }}
                          disabled={updatingIds.has(`${viewingDrone.droneId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingDrone.droneId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-reject"
                          onClick={() => {
                            updateDroneStatus(viewingDrone.droneId, "rejected");
                            closeDroneDetails();
                          }}
                          disabled={updatingIds.has(`${viewingDrone.droneId}-rejected`)}
                          title="Reject"
                        >
                          {updatingIds.has(`${viewingDrone.droneId}-rejected`) ? "..." : "✗ Reject"}
                        </button>
                      </>
                    )}

                    {viewingDrone.status === "rejected" && (
                      <>
                        <button
                          className="action-btn-large action-pending"
                          onClick={() => {
                            updateDroneStatus(viewingDrone.droneId, "pending");
                            closeDroneDetails();
                          }}
                          disabled={updatingIds.has(`${viewingDrone.droneId}-pending`)}
                          title="Move to Pending"
                        >
                          {updatingIds.has(`${viewingDrone.droneId}-pending`) ? "..." : "⏳ To Pending"}
                        </button>
                        <button
                          className="action-btn-large action-approve"
                          onClick={() => {
                            updateDroneStatus(viewingDrone.droneId, "approved");
                            closeDroneDetails();
                          }}
                          disabled={updatingIds.has(`${viewingDrone.droneId}-approved`)}
                          title="Approve"
                        >
                          {updatingIds.has(`${viewingDrone.droneId}-approved`) ? "..." : "✓ Approve"}
                        </button>
                      </>
                    )}


                    <button
                      className="action-btn-large action-delete"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this drone?")) {
                          deleteDrone(viewingDrone.droneId);
                          closeDroneDetails();
                        }
                      }}
                      title="Delete"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {viewingDescription && (
        <div className="modal-overlay" onClick={closeDescription}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Description</h2>
              <button className="close-btn" onClick={closeDescription}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap' }}>
                {viewingDescription.description}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default Drones;